import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Check, ChevronLeft, ChevronRight, Loader2, Rocket, Search, ShieldCheck, Users } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

// Wave B — guided Climara survey launch wizard. The final step submits a
// campaign request to Corporate Talent Hub (Climara remains a consulting engagement).
export default function ClimaraLaunchWizard({ open, onClose, onSubmitted }) {
  const { i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');

  const [step, setStep] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sending, setSending] = useState(false);

  const [name, setName] = useState('');
  const [waveLabel, setWaveLabel] = useState('');
  const [audience, setAudience] = useState('all');
  const [deptIds, setDeptIds] = useState([]);
  const [empIds, setEmpIds] = useState([]);
  const [empQuery, setEmpQuery] = useState('');
  const [deadline, setDeadline] = useState('');
  const [anonNote, setAnonNote] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep(0); setName(''); setWaveLabel(''); setAudience('all');
    setDeptIds([]); setEmpIds([]); setDeadline(''); setAnonNote(''); setNotes('');
    (async () => {
      const [deps, emps] = await Promise.all([
        clientApi.listDepartments().catch(() => []),
        clientApi.listCollaborators().catch(() => []),
      ]);
      setDepartments(Array.isArray(deps) ? deps : []);
      setEmployees((Array.isArray(emps) ? emps : []).filter((u) => u.role === 'evaluated'
        && (u.employment_status || 'active') !== 'former'));
    })();
  }, [open]);

  const year = new Date().getFullYear();
  const suggestions = [
    `Q1 ${year}`, `Q2 ${year}`, `Q3 ${year}`, `Q4 ${year}`,
    isES ? `Encuesta Anual ${year}` : `Annual Survey ${year}`,
    isES ? 'Encuesta de Liderazgo' : 'Leadership Survey',
  ];

  const filteredEmps = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => (e.name || '').toLowerCase().includes(q));
  }, [employees, empQuery]);

  const recipientCount = useMemo(() => {
    if (audience === 'all') return employees.length;
    if (audience === 'departments') return employees.filter((e) => deptIds.includes(e.department_id)).length;
    return empIds.length;
  }, [audience, deptIds, empIds, employees]);

  const stepValid = () => {
    if (step === 0) return name.trim().length >= 3 && waveLabel.trim().length >= 2;
    if (step === 1) {
      if (audience === 'departments') return deptIds.length > 0;
      if (audience === 'employees') return empIds.length > 0;
      return true;
    }
    return true;
  };

  const submit = async () => {
    setSending(true);
    try {
      await clientApi.climaraCreateCampaignRequest({
        name: name.trim(),
        wave_label: waveLabel.trim(),
        target_audience: audience,
        department_ids: audience === 'departments' ? deptIds : [],
        employee_ids: audience === 'employees' ? empIds : [],
        anonymity_note: anonNote || null,
        deadline: deadline || null,
        notes: notes || null,
      });
      toast.success(isES ? 'Solicitud enviada a Corporate Talent Hub' : 'Request sent to Corporate Talent Hub', {
        description: isES
          ? 'Tu consultor validará y lanzará la medición.'
          : 'Your advisor will validate and launch the wave.',
      });
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(isES ? 'No se pudo enviar' : 'Could not submit', { description: formatApiError(e) });
    } finally { setSending(false); }
  };

  const STEPS = isES
    ? ['Medición', 'Audiencia', 'Configuración', 'Revisión']
    : ['Wave', 'Audience', 'Settings', 'Review'];

  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-xl" data-testid="climara-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            {isES ? 'Nueva medición de clima' : 'New climate wave'}
          </DialogTitle>
          <DialogDescription>
            {isES
              ? 'Configura tu medición paso a paso. Corporate Talent Hub la validará y lanzará.'
              : 'Configure your wave step by step. Corporate Talent Hub validates and launches it.'}
          </DialogDescription>
        </DialogHeader>

        {/* stepper */}
        <div className="flex items-center gap-1.5" data-testid="climara-wizard-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${i === step ? 'text-primary' : i < step ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                <span className={`h-5 w-5 rounded-full inline-flex items-center justify-center text-xs border
                  ${i === step ? 'border-primary bg-primary/10' : i < step ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-border'}`}>
                  {i < step ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                {s}
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
            </React.Fragment>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-3" data-testid="climara-wizard-step-wave">
            <div className="space-y-1">
              <Label>{isES ? 'Nombre de la medición' : 'Wave name'}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder={isES ? 'Ej. Encuesta de clima Q1' : 'e.g. Q1 climate survey'}
                     data-testid="climara-wizard-name" />
            </div>
            <div className="space-y-1">
              <Label>{isES ? 'Etiqueta de medición (para comparar en el tiempo)' : 'Wave label (used for trend comparison)'}</Label>
              <Input value={waveLabel} onChange={(e) => setWaveLabel(e.target.value)}
                     placeholder={`Q1 ${year}`} data-testid="climara-wizard-wave-label" />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => setWaveLabel(s)}
                          className="text-xs px-2 py-0.5 rounded-full border hover:bg-muted"
                          data-testid={`climara-wizard-suggestion-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="climara-wizard-step-audience">
            <div className="space-y-1">
              <Label>{isES ? 'Destinatarios' : 'Recipients'}</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger data-testid="climara-wizard-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isES ? 'Toda la empresa' : 'Whole company'}</SelectItem>
                  <SelectItem value="departments">{isES ? 'Departamentos seleccionados' : 'Selected departments'}</SelectItem>
                  <SelectItem value="employees">{isES ? 'Empleados específicos' : 'Specific employees'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {audience === 'departments' && (
              <ul className="border rounded-md divide-y max-h-44 overflow-y-auto" data-testid="climara-wizard-dept-list">
                {departments.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-muted/40"
                      onClick={() => toggle(deptIds, setDeptIds, d.id)}>
                    <Checkbox checked={deptIds.includes(d.id)} onCheckedChange={() => toggle(deptIds, setDeptIds, d.id)} />
                    <span className="text-xs font-medium">{d.name_es || d.name}</span>
                  </li>
                ))}
              </ul>
            )}
            {audience === 'employees' && (
              <div className="space-y-1">
                <div className="relative">
                  <Search className="h-3 w-3 absolute top-2.5 left-2.5 text-muted-foreground" />
                  <Input value={empQuery} onChange={(e) => setEmpQuery(e.target.value)} className="pl-7 h-8 text-xs"
                         placeholder={isES ? 'Buscar…' : 'Search…'} data-testid="climara-wizard-emp-search" />
                </div>
                <ul className="border rounded-md divide-y max-h-44 overflow-y-auto" data-testid="climara-wizard-emp-list">
                  {filteredEmps.map((e) => (
                    <li key={e.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-muted/40"
                        onClick={() => toggle(empIds, setEmpIds, e.id)}>
                      <Checkbox checked={empIds.includes(e.id)} onCheckedChange={() => toggle(empIds, setEmpIds, e.id)} />
                      <span className="text-xs font-medium">{e.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="climara-wizard-recipient-count">
              <Users className="h-3 w-3" />
              {recipientCount} {isES ? 'destinatarios estimados' : 'estimated recipients'}
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="climara-wizard-step-settings">
            <div className="rounded-md border bg-emerald-50/50 border-emerald-200 p-3 flex gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <p className="text-xs text-emerald-800">
                {isES
                  ? 'Climara es siempre 100% anónima: las respuestas se agregan por departamento y nunca se muestran datos individuales.'
                  : 'Climara is always 100% anonymous: responses are aggregated by department and individual data is never shown.'}
              </p>
            </div>
            <div className="space-y-1">
              <Label>{isES ? 'Nota de anonimato para colaboradores (opcional)' : 'Anonymity note for employees (optional)'}</Label>
              <Textarea rows={2} value={anonNote} onChange={(e) => setAnonNote(e.target.value)}
                        placeholder={isES ? 'Mensaje que refuerce la confidencialidad…' : 'Message reinforcing confidentiality…'}
                        data-testid="climara-wizard-anon-note" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{isES ? 'Fecha límite (opcional)' : 'Deadline (optional)'}</Label>
                <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                       data-testid="climara-wizard-deadline" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>{isES ? 'Notas para Corporate Talent Hub (opcional)' : 'Notes for Corporate Talent Hub (optional)'}</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        data-testid="climara-wizard-notes" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 text-sm" data-testid="climara-wizard-step-review">
            <div className="rounded-md border divide-y">
              {[
                [isES ? 'Medición' : 'Wave', `${name} · ${waveLabel}`],
                [isES ? 'Destinatarios' : 'Recipients',
                  audience === 'all' ? (isES ? `Toda la empresa (${recipientCount})` : `Whole company (${recipientCount})`)
                    : audience === 'departments' ? (isES ? `${deptIds.length} departamento(s) · ${recipientCount} colaboradores` : `${deptIds.length} department(s) · ${recipientCount} employees`)
                      : (isES ? `${empIds.length} empleados seleccionados` : `${empIds.length} selected employees`)],
                [isES ? 'Anonimato' : 'Anonymity', isES ? '100% anónima (siempre)' : '100% anonymous (always)'],
                [isES ? 'Fecha límite' : 'Deadline', deadline || '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-3 py-2">
                  <span className="text-xs text-muted-foreground">{k}</span>
                  <span className="text-xs font-medium text-right">{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <CalendarClock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {isES
                ? 'Al enviar, Corporate Talent Hub validará la configuración, seleccionará el cuestionario certificado y lanzará las invitaciones.'
                : 'On submit, Corporate Talent Hub validates the configuration, selects the certified questionnaire and launches the invitations.'}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={sending} data-testid="climara-wizard-back">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />{isES ? 'Atrás' : 'Back'}
            </Button>
          )}
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!stepValid()} data-testid="climara-wizard-next">
              {isES ? 'Siguiente' : 'Next'}<ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={sending} data-testid="climara-wizard-submit">
              {sending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
              {isES ? 'Enviar a Corporate Talent Hub' : 'Send to Corporate Talent Hub'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
